import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Tabs, Alert,Col, Row, Spin, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { createRF_module, updateRF_module, deleteRF_module, getRF_moduleByExploitation, getRF_moduleInfo } from "../../Utils/exploitations/RF_module";
import { createModem, updateModem, deleteModem, getModemByExploitation, getModemInfo } from "../../Utils/exploitations/modems";

const { TabPane } = Tabs;

const HardwareParam = ({ exploitationId }) => {
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState('modem');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modemData, setModemData] = useState(null);
    const [rfModuleData, setRfModuleData] = useState(null);

    useEffect(() => {
        if (exploitationId) {
            fetchData();
        }
    }, [exploitationId, activeTab, form, setModemData, setRfModuleData]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [modem] = await getModemByExploitation(exploitationId);
            const [rfModule] = await getRF_moduleByExploitation(exploitationId);

            if (modem) {
                const modemInfo = await getModemInfo(modem.modem_id);
                setModemData(modemInfo.data);
                form.setFieldsValue({ modem: modemInfo.data });
            }


            if (rfModule) {
                const rfModuleInfo = await getRF_moduleInfo(rfModule.rf_module_id);
                setRfModuleData(rfModuleInfo.data);
                form.setFieldsValue({ rfModule: rfModuleInfo.data });
            }
        } catch (err) {
            setError("Échec de la récupération des données matérielles. Veuillez réessayer.");
            message.error("Échec de la récupération des données matérielles.");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            setError(null);

            if (activeTab === 'modem') {
                if (modemData) {
                    await updateModem(modemData.modem_id, { ...values.modem, exploitation_id: exploitationId });
                } else {
                    await createModem({ ...values.modem, exploitation_id: exploitationId });
                }
            } else {
                if (rfModuleData) {
                    await updateRF_module(rfModuleData.rf_module_id, { ...values.rfModule, exploitation_id: exploitationId });
                } else {
                    await createRF_module({ ...values.rfModule, exploitation_id: exploitationId });
                }
            }
            message.success("Données enregistrées avec succès.");
            setModemData(null);
            setRfModuleData(null);
        } catch (err) {
            setError("Échec de l'enregistrement des données. Veuillez réessayer.");
            message.error("Échec de l'enregistrement des données.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            setError(null);

            if (activeTab === 'modem' && modemData) {
                await deleteModem(modemData.modem_id);
                setModemData(null);
                form.resetFields();
                message.success("Modem supprimé avec succès.");
            } else if (activeTab === 'rfModule' && rfModuleData) {
                await deleteRF_module(rfModuleData.rf_module_id);
                setRfModuleData(null);
                form.resetFields();
                message.success("Module RF supprimé avec succès.");
            }
        } catch (err) {
            setError("Échec de la suppression des données. Veuillez réessayer.");
            message.error("Échec de la suppression des données.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <Spin size="large" />
                </div>
            ) : (
                <Form form={form} layout="vertical">
                    {error && <Alert message={error} type="error" showIcon className="mb-4" />}
                    <Tabs activeKey={activeTab} onChange={setActiveTab}>

                        <TabPane tab="Modem" key="modem">
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['modem', 'modem_manual_id']} label="ID Manuel du Modem"
                                        rules={[{ required: true, message: 'Veuillez entrer l\'ID Manuel du Modem' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['modem', 'ip_address']} label="Adresse IP">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['modem', 'telephone_number']} label="Numéro de Téléphone"
                                        rules={[{ required: true, message: 'Veuillez entrer le Numéro de Téléphone' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['modem', 'pin_code']} label="Code PIN">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['modem', 'puk_code']} label="Code PUK">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['modem', 'serial_number']} label="Numéro de Série">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['modem', 'operator']} label="Opérateur">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name={['modem', 'brand_name']} label="Marque">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name={['modem', 'model_name']} label="Modèle">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </TabPane>

                        <TabPane tab="Module RF" key="rfModule">
                            <Form.Item name={['rfModule', 'speed_class']} label="Classe de Vitesse">
                                <Input />
                            </Form.Item>
                            <Form.Item name={['rfModule', 'transmitted_power']} label="Puissance Transmise">
                                <Input />
                            </Form.Item>
                            <Form.Item name={['rfModule', 'destination_address']} label="Adresse de Destination">
                                <Input />
                            </Form.Item>
                        </TabPane>
                    </Tabs>
                    <div className="flex justify-between mt-4">
                        <Button
                            onClick={handleDelete}
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={loading || (!modemData && !rfModuleData)}
                        >
                            Supprimer
                        </Button>
                        <div className="flex gap-2">
                            <Button
                                onClick={handleSubmit}
                                type="primary"
                                icon={<PlusOutlined />}
                                disabled={loading}
                            >
                                {modemData || rfModuleData ? "Mettre à jour" : "Ajouter"}
                            </Button>
                        </div>
                    </div>
                </Form>
            )}
        </>
    );
};

export default HardwareParam;
